import { ReactNode, useState } from 'react';
import { Menu } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface LayoutProps {
  topBarLeft?: ReactNode;
  topBarRight?: ReactNode;
  sidebar?: ReactNode;
  children: ReactNode;
}

export const Layout = ({ topBarLeft, topBarRight, sidebar, children }: LayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground selection:bg-primary/10">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      {sidebar && (
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r bg-card transition-transform duration-300 ease-in-out lg:static lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {sidebar}
        </aside>
      )}

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="z-10 flex h-16 shrink-0 items-center justify-between border-b bg-card px-4 sm:px-6">
          <div className="flex items-center gap-3">
            {sidebar && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
                aria-label="Open navigation"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            {topBarLeft}
          </div>
          <div className="flex items-center gap-2">{topBarRight}</div>
        </header>

        {/* Content */}
        <main className="flex flex-1 flex-col overflow-hidden">{children}</main>
      </div>
    </div>
  );
};
